/**
 * Compile-time organization-scoping for the generated API client.
 *
 * The generated fetchers send `x-organization-id` at runtime (via the shared
 * transport's `orgHeaders()`), but nothing stops a caller from invoking an
 * org-scoped endpoint when there is no active org — today that surfaces only
 * as a 400 `no_active_organization` at runtime.
 *
 * {@link OrgId} is a branded string: a plain `string` is NOT assignable to it,
 * so the org-scoped facade hooks in `./agents.ts` can take an `OrgId` as a
 * required argument and the compiler rejects any call site that forgets the
 * org. Resolve one with {@link useActiveOrgId} (which returns `null` when no
 * org is selected, forcing the caller to handle that case) or brand a known
 * id with {@link asOrgId}.
 *
 * TODO(org-path): once org-scoped routes move under `/orgs/{slug}` the org
 * becomes a required path param and this brand can be replaced by the
 * generated path-parameter argument.
 */
import { useOrgKey } from '../PluginClientContext';

export type OrgId = string & { readonly __brand: 'OrgId' };

/**
 * Brand a known organization id or slug as an {@link OrgId}. Use only where an
 * active org has genuinely been established (a route param, a membership).
 */
export function asOrgId(id: string): OrgId {
  return id as OrgId;
}

/**
 * The active organization as a branded {@link OrgId}, or `null` when none is
 * selected (root routes such as `/profile`). Because the org-scoped facade
 * hooks require a non-null `OrgId`, a missing org becomes a compile-time
 * obligation at the call site rather than a runtime 400.
 */
export function useActiveOrgId(): OrgId | null {
  const orgKey = useOrgKey();
  // `useOrgKey()` returns the sentinel `'global'` when no org is active.
  return orgKey && orgKey !== 'global' ? asOrgId(orgKey) : null;
}
